import type { ChangeEvent, FC } from "react";
import { useMemo } from "react";

import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { UiCheckIcon } from "@jti/brand-icons/ui-check";
import { UiInfoCircleBoldIcon } from "@jti/brand-icons/ui-info-circle-bold";
import { Button } from "@jti/core-components/components/Button";
import { Loader } from "@jti/core-components/components/Loader";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import styles from "./PackCodeInputSection.module.scss";

const { sanitize } = dompurify;

type PackCodeInputSectionProps = {
  inputPackCode: string;
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleClick: () => void;
  hasCodeError: boolean;
  hasEarningError: boolean;
  isValid: boolean;
  limitReached: boolean;
  isQueryInProgress: boolean;
  inputPlaceholder: string;
  earnedPointsMessage: string;
  errorCodeMessage: string;
  errorEarningMessage: string;
  limitTextReached: string;
  buttonTitle: string;
  errorCode?: string;
};

type MessageIconProps = {
  error: boolean;
  htmlMessage?: string | Node;
};

const MessageIcon = ({ error, htmlMessage }: MessageIconProps) => {
  return (
    <div className={styles.messageWrapper}>
      {error ? (
        <UiInfoCircleBoldIcon
          width="12px"
          height="12px"
          fill="#ffffff"
          className={styles.icon}
          data-testid="packCodesConfirmationIcon"
        />
      ) : (
        <UiCheckIcon
          width="12px"
          height="12px"
          fill="currentColor"
          className={styles.icon}
          data-testid="packCodesConfirmationIcon"
        />
      )}

      {htmlMessage && <span dangerouslySetInnerHTML={{ __html: sanitize(htmlMessage) }} />}
    </div>
  );
};

export const PackCodeInputSection: FC<PackCodeInputSectionProps> = ({
  inputPackCode,
  handleInputChange,
  handleClick,
  hasCodeError,
  hasEarningError,
  isValid,
  limitReached,
  isQueryInProgress,
  inputPlaceholder,
  earnedPointsMessage,
  errorCodeMessage,
  errorEarningMessage,
  limitTextReached,
  buttonTitle,
  errorCode,
}) => {
  const { __ } = useTranslations();

  const inputClassNames = classNames(styles.input, {
    [styles.error]: hasCodeError || hasEarningError || limitReached,
  });

  const errorMessage = useMemo(() => {
    if (!errorCode) {
      return errorCodeMessage;
    }

    return __(`pack_codes::${errorCode}`, undefined, errorCodeMessage);
  }, [hasCodeError, errorCode, errorCodeMessage]);

  return (
    <section className={styles.section}>
      <div className={styles.inputWrapper}>
        <input
          type="text"
          className={inputClassNames}
          onChange={handleInputChange}
          placeholder={inputPlaceholder}
          value={inputPackCode}
        />
        {isValid && !hasEarningError && !!earnedPointsMessage && !isQueryInProgress && (
          <MessageIcon error={false} htmlMessage={earnedPointsMessage} />
        )}
        {hasCodeError && !limitReached && !hasEarningError && <MessageIcon error={true} htmlMessage={errorMessage} />}
        {hasEarningError && <MessageIcon error={true} htmlMessage={errorEarningMessage} />}
        {limitReached && <MessageIcon error={false} htmlMessage={limitTextReached} />}
      </div>
      {!isQueryInProgress && (
        <div className={classNames(styles.buttonWrapper, "dark-theme")}>
          <Button data-testid="packCodesButton" onClick={handleClick} variant="primary" className={styles.button}>
            {buttonTitle}
          </Button>
        </div>
      )}
      {isQueryInProgress && (
        <div className={styles.buttonLoader}>
          <Loader />
        </div>
      )}
    </section>
  );
};
